import { useState } from 'react'
import { Modal } from '@shared/ui/Modal'
import { ENV } from '@shared/config/env'
import styles from './CancelAutoRenewConfirmModal.module.css'

interface CancelAutoRenewConfirmModalProps {
  isSubmitting?: boolean
  errorText?: string | null
  /** `reason` — текст причины или null, если выбрано «без причины» */
  onConfirm: (reason: string | null) => void
  onClose: () => void
}

const PRESET_REASONS = [
  'Не нашла (-ел) нужные рецепты',
  'Слишком сложно / непонятно, как пользоваться',
  'Финансовая причина',
  'Просто пауза — вернусь позже',
] as const

type SelectedReason =
  | { kind: 'preset'; value: (typeof PRESET_REASONS)[number] }
  | { kind: 'other' }
  | { kind: 'none' }

const OTHER_REASON_MAX = 1024
// Префикс "Другое: " считается в общий лимит reason на бэке (1024 символа)
const OTHER_PREFIX = 'Другое: '
const OTHER_TEXT_MAX = OTHER_REASON_MAX - OTHER_PREFIX.length

/**
 * Модалка отмены автопродления с выбором причины (radio).
 * Сначала пользователь выбирает причину, затем жмёт «Отменить подписку».
 */
export const CancelAutoRenewConfirmModal = ({
  isSubmitting = false,
  errorText,
  onConfirm,
  onClose,
}: CancelAutoRenewConfirmModalProps) => {
  const variant = ENV.IS_TG ? 'bottomSheet' : 'center'
  const [selected, setSelected] = useState<SelectedReason | null>(null)
  const [otherText, setOtherText] = useState('')

  // Кнопка «Отменить подписку» неактивна, пока не выбрана причина (или «без причины»).
  // Для kind='other' дополнительно требуется непустой текст.
  const canSubmit = (() => {
    if (!selected) return false
    if (selected.kind === 'other') return otherText.trim().length > 0
    return true
  })()

  const buildReason = (): string | null => {
    if (!selected) return null
    if (selected.kind === 'preset') return selected.value
    if (selected.kind === 'other') return `${OTHER_PREFIX}${otherText.trim()}`
    return null
  }

  const handleSubmit = () => {
    if (isSubmitting || !canSubmit) return
    onConfirm(buildReason())
  }

  return (
    <Modal onClose={onClose} variant={variant} showClose>
      <div className={styles.content}>
        <h3 className={styles.title}>Очень жаль, что ты уходишь 😔</h3>
        <p className={styles.text}>
          Пожалуйста, расскажи, почему возникло такое решение? Это поможет
          сделать сервис лучше.
        </p>

        <div className={styles.options}>
          {PRESET_REASONS.map(reason => {
            const isActive =
              selected?.kind === 'preset' && selected.value === reason
            return (
              <button
                key={reason}
                type="button"
                className={`${styles.optionButton} ${isActive ? styles.optionButtonActive : ''}`}
                onClick={() => setSelected({ kind: 'preset', value: reason })}
                disabled={isSubmitting}
                aria-pressed={isActive}
              >
                {reason}
              </button>
            )
          })}

          <button
            type="button"
            className={`${styles.optionButton} ${selected?.kind === 'other' ? styles.optionButtonActive : ''}`}
            onClick={() => setSelected({ kind: 'other' })}
            disabled={isSubmitting}
            aria-pressed={selected?.kind === 'other'}
          >
            Другое
          </button>

          {selected?.kind === 'other' && (
            <textarea
              className={styles.otherTextarea}
              placeholder="Напиши причину…"
              value={otherText}
              onChange={e =>
                setOtherText(e.target.value.slice(0, OTHER_TEXT_MAX))
              }
              maxLength={OTHER_TEXT_MAX}
              rows={3}
              disabled={isSubmitting}
              autoFocus
            />
          )}

          <button
            type="button"
            className={`${styles.optionButton} ${selected?.kind === 'none' ? styles.optionButtonActive : ''}`}
            onClick={() => setSelected({ kind: 'none' })}
            disabled={isSubmitting}
            aria-pressed={selected?.kind === 'none'}
          >
            Отменить без причины
          </button>
        </div>

        {errorText && <p className={styles.error}>{errorText}</p>}

        <button
          type="button"
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={isSubmitting || !canSubmit}
        >
          {isSubmitting ? 'Отменяем…' : 'Отменить подписку'}
        </button>
      </div>
    </Modal>
  )
}
